import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as XLSX from 'xlsx';
import { Download, Upload, ChevronDown, ChevronUp, Loader2, AlertCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type ErrorRecord = {
  rowNumber: number;
  surname?: string;
  other_names?: string;
  class?: string;
  stream?: string;
  error: string;
  originalData: any;
};

export function StudentBulkActions() {
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingValidations, setIsLoadingValidations] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [validClasses, setValidClasses] = useState<string[]>([]);
  const [validStreams, setValidStreams] = useState<string[]>([]);
  const [errorRecords, setErrorRecords] = useState<ErrorRecord[]>([]);
  const [hasUploadErrors, setHasUploadErrors] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.schoolId) {
      loadValidClassesAndStreams();
    }
  }, [user?.schoolId]);

  const loadValidClassesAndStreams = async () => {
    if (!user?.schoolId) return;

    setIsLoadingValidations(true);
    try {
      // Fetch valid classes for the school
      const { data: classesData, error: classesError } = await supabase
        .from('school_classes')
        .select('name')
        .eq('school_id', user.schoolId);

      if (classesError) throw classesError;
      
      const classes = classesData?.map(c => c.name) || [];
      setValidClasses(classes);
      console.log('Loaded valid classes:', classes);

      // Fetch valid streams for the school
      const { data: streamsData, error: streamsError } = await supabase
        .from('class_streams')
        .select('name')
        .eq('school_id', user.schoolId);

      if (streamsError) throw streamsError;
      
      const streams = streamsData?.map(s => s.name) || [];
      setValidStreams(streams);
      console.log('Loaded valid streams:', streams);

    } catch (error) {
      console.error('Error loading classes and streams:', error);
      toast({
        title: "Error",
        description: "Failed to load valid classes and streams. Please refresh the page.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingValidations(false);
    }
  };

  const normalizeClassName = (className: string) => {
    if (!className) return '';
    return className.trim();
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    
    // Create template sheet
    const templateData = [{
      SURNAME: 'Example',
      OTHER_NAMES: 'Student',
      CLASS: validClasses.length > 0 ? validClasses[0] : 'Class Name',
      STREAM: 'Stream Name (Optional)',
      SECTION: 'Section (Optional)',
      DORMITORY: 'Dormitory Name (Optional)',
      DORMITORY_MOTHER: 'Dormitory Mother Name (Optional)',
      DORMITORY_MOTHER_CONTACT: 'Dormitory Mother Contact (Optional)',
      TUTOR1_NAME: 'Tutor 1 Name (Optional)',
      TUTOR1_CONTACT: 'Tutor 1 Contact (Optional)',
      TUTOR2_NAME: 'Tutor 2 Name (Optional)',
      TUTOR2_CONTACT: 'Tutor 2 Contact (Optional)',
      EMERGENCY_CONTACT_NAME: 'Emergency Contact Name (Optional)',
      EMERGENCY_CONTACT_PHONE: 'Emergency Contact Phone (Optional)',
    }];

    const ws = XLSX.utils.json_to_sheet(templateData);

    // Create validation sheet
    const validationData = [
      ['Valid Classes', 'Valid Streams'],
      ...Array.from({ length: Math.max(validClasses.length, validStreams.length) }, (_, i) => [
        validClasses[i] || '',
        validStreams[i] || ''
      ])
    ];

    const validationWs = XLSX.utils.aoa_to_sheet(validationData);

    // Add both sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.utils.book_append_sheet(wb, validationWs, 'Valid Values');

    XLSX.writeFile(wb, 'student_upload_template.xlsx');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.schoolId) {
      toast({
        title: "Error",
        description: "Please select a file and ensure you're logged in",
        variant: "destructive",
      });
      return;
    }

    if (validClasses.length === 0) {
      toast({
        title: "Error",
        description: "No valid classes loaded. Please refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setErrorRecords([]);
    setHasUploadErrors(false);
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      let successCount = 0;
      let errorCount = 0;
      const newErrorRecords: ErrorRecord[] = [];
      const totalStudents = jsonData.length;

      // First, check for duplicates within the Excel file itself
      const duplicatesInFile = new Map();
      (jsonData as any[]).forEach((row, index) => {
        const key = `${row.SURNAME?.toLowerCase()}_${row['OTHER_NAMES']?.toLowerCase()}_${row.CLASS?.toLowerCase()}`;
        if (duplicatesInFile.has(key)) {
          duplicatesInFile.get(key).push(index + 2); // Add 2 for Excel row number (1-based + header)
        } else {
          duplicatesInFile.set(key, [index + 2]);
        }
      });

      // Report duplicates within the file
      duplicatesInFile.forEach((rows, key) => {
        if (rows.length > 1) {
          const [surname, otherNames, className] = key.split('_');
          newErrorRecords.push({
            rowNumber: rows[0], // Use the first row number for the error record
            error: `Duplicate entries found in rows ${rows.join(', ')} for student: ${surname}${otherNames ? `, ${otherNames}` : ''} in class ${className}`,
            originalData: { SURNAME: surname, OTHER_NAMES: otherNames, CLASS: className }
          });
          errorCount += rows.length - 1; // Count all but one occurrence as errors
        }
      });

      for (const [index, row] of Object.entries(jsonData as any[])) {
        try {
          setProgress(Math.round((Number(index) / totalStudents) * 100));

          if (!row.SURNAME || !row.CLASS) {
            errorCount++;
            newErrorRecords.push({
              rowNumber: Number(index) + 2,
              error: `Missing required field${!row.SURNAME && !row.CLASS ? 's' : ''} (${!row.SURNAME ? 'SURNAME' : ''}${!row.SURNAME && !row.CLASS ? ' and ' : ''}${!row.CLASS ? 'CLASS' : ''})`,
              originalData: row,
            });
            continue;
          }

          // Validate class
          const normalizedClass = normalizeClassName(row.CLASS);
          if (!validClasses.includes(normalizedClass)) {
            errorCount++;
            newErrorRecords.push({
              rowNumber: Number(index) + 2,
              error: `Invalid class "${normalizedClass}". Valid classes are: ${validClasses.join(', ')}`,
              originalData: row,
            });
            continue;
          }

          // Validate stream if provided
          if (row.STREAM && !validStreams.includes(row.STREAM.trim())) {
            errorCount++;
            newErrorRecords.push({
              rowNumber: Number(index) + 2,
              error: `Invalid stream "${row.STREAM}". Valid streams are: ${validStreams.join(', ')}`,
              originalData: row,
            });
            continue;
          }

          // Check if student already exists in database
          const { data: existingStudents, error: checkError } = await supabase
            .from('students')
            .select('id, surname, other_names, class')
            .eq('school_id', user.schoolId)
            .ilike('surname', row.SURNAME)
            .eq('class', normalizedClass);

          if (checkError) {
            console.error('Error checking for existing student:', checkError);
            errorCount++;
            newErrorRecords.push({
              rowNumber: Number(index) + 2,
              error: `Database error while checking for duplicates`,
              originalData: row,
            });
            continue;
          }

          // Check for exact match including other_names if provided
          const isDuplicate = existingStudents?.some(existing => 
            existing.surname.toLowerCase() === row.SURNAME.toLowerCase() &&
            ((!existing.other_names && !row['OTHER_NAMES']) ||
             (existing.other_names?.toLowerCase() === row['OTHER_NAMES']?.toLowerCase())) &&
            existing.class.toLowerCase() === normalizedClass.toLowerCase()
          );

          if (isDuplicate) {
            errorCount++;
            newErrorRecords.push({
              rowNumber: Number(index) + 2,
              error: `Student already exists - ${row.SURNAME}${row['OTHER_NAMES'] ? `, ${row['OTHER_NAMES']}` : ''} in class ${normalizedClass}`,
              originalData: row,
            });
            continue;
          }

          const { error: insertError } = await supabase
            .from('students')
            .insert([{
              surname: row.SURNAME,
              other_names: row['OTHER_NAMES'] || null,
              class: normalizedClass,
              stream: row.STREAM || null,
              school_id: user.schoolId,
              section: row.SECTION || null,
              dormitory: row.DORMITORY || null,
              dormitory_mother: row.DORMITORY_MOTHER || null,
              dormitory_mother_contact: row.DORMITORY_MOTHER_CONTACT || null,
              tutor1_name: row.TUTOR1_NAME || null,
              tutor1_contact: row.TUTOR1_CONTACT || null,
              tutor2_name: row.TUTOR2_NAME || null,
              tutor2_contact: row.TUTOR2_CONTACT || null,
              emergency_contact_name: row.EMERGENCY_CONTACT_NAME || null,
              emergency_contact_phone: row.EMERGENCY_CONTACT_PHONE || null,
            }]);

          if (insertError) {
            errorCount++;
            newErrorRecords.push({
              rowNumber: Number(index) + 2,
              error: insertError.message,
              originalData: row,
            });
          } else {
            successCount++;
          }
        } catch (error) {
          errorCount++;
          newErrorRecords.push({
            rowNumber: Number(index) + 2,
            error: 'Unexpected error during processing',
            originalData: row,
          });
        }
      }

      setErrorRecords(newErrorRecords);
      setHasUploadErrors(errorCount > 0);

      if (errorCount > 0) {
        downloadErrorReport();
        toast({
          title: successCount > 0 ? "Partial Success" : "Upload Failed",
          description: `Successfully added ${successCount} students. Failed to add ${errorCount} students. Error report has been downloaded.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: `Successfully added all ${successCount} students.`,
        });
      }

    } catch (error) {
      console.error('Error uploading students:', error);
      toast({
        title: "Error",
        description: "Failed to process the file. Please check the format and try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setProgress(0);
      e.target.value = '';
    }
  };

  const downloadErrorReport = () => {
    if (errorRecords.length === 0) return;

    const wb = XLSX.utils.book_new();
    const errorData = errorRecords.map(record => ({
      'Row Number': record.rowNumber,
      'Error Message': record.error,
      'Surname': record.originalData.SURNAME || '',
      'Other Names': record.originalData.OTHER_NAMES || '',
      'Class': record.originalData.CLASS || '',
      'Stream': record.originalData.STREAM || '',
      'Section': record.originalData.SECTION || '',
      'Dormitory': record.originalData.DORMITORY || '',
      'Dormitory Mother': record.originalData.DORMITORY_MOTHER || '',
      'Dormitory Mother Contact': record.originalData.DORMITORY_MOTHER_CONTACT || '',
      'Tutor 1 Name': record.originalData.TUTOR1_NAME || '',
      'Tutor 1 Contact': record.originalData.TUTOR1_CONTACT || '',
      'Tutor 2 Name': record.originalData.TUTOR2_NAME || '',
      'Tutor 2 Contact': record.originalData.TUTOR2_CONTACT || '',
      'Emergency Contact Name': record.originalData.EMERGENCY_CONTACT_NAME || '',
      'Emergency Contact Phone': record.originalData.EMERGENCY_CONTACT_PHONE || '',
    }));

    const ws = XLSX.utils.json_to_sheet(errorData);
    XLSX.utils.book_append_sheet(wb, ws, 'Upload Errors');
    XLSX.writeFile(wb, 'student_upload_errors.xlsx');
  };

  return (
    <Card>
      <CardHeader 
        className="cursor-pointer" 
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Bulk Student Upload</CardTitle>
            <CardDescription>
              Upload multiple students or download a template
            </CardDescription>
          </div>
          {isCollapsed ? <ChevronDown className="h-6 w-6" /> : <ChevronUp className="h-6 w-6" />}
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4">
            {isLoadingValidations ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading valid classes and streams...
              </div>
            ) : (
              <>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  disabled={isUploading || isLoadingValidations || validClasses.length === 0}
                />
                {validClasses.length === 0 && !isLoadingValidations && (
                  <p className="text-sm text-destructive">
                    No valid classes found. Please ensure classes are set up in the system.
                  </p>
                )}
              </>
            )}
            {isUploading && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground">
                  Uploading... {progress}%
                </p>
              </div>
            )}
            {hasUploadErrors && (
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <p className="text-sm text-destructive">
                  Some students could not be added. Download the error report to see details.
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="w-full sm:w-auto"
              disabled={isUploading || isLoadingValidations}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </Button>
            {hasUploadErrors && (
              <Button
                variant="destructive"
                onClick={downloadErrorReport}
                className="w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Error Report
              </Button>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            Upload an Excel file with student information. Required columns: SURNAME, CLASS. Optional: STREAM, OTHER_NAMES, SECTION, DORMITORY, DORMITORY_MOTHER, DORMITORY_MOTHER_CONTACT, TUTOR1_NAME, TUTOR1_CONTACT, TUTOR2_NAME, TUTOR2_CONTACT, EMERGENCY_CONTACT_NAME, EMERGENCY_CONTACT_PHONE. 
            The CLASS field must contain a valid class name, while STREAM is optional but must be valid if provided.
          </div>
        </CardContent>
      )}
    </Card>
  );
}
